// Org Settings sub-section: Opportunity Engine pricing defaults.
//
// Surfaces `organizations.default_commission_pct` and
// `organizations.default_margin_pct` for editing by an org admin/owner.
// These prefill the Opportunity Engine pricing form (Phase 1 Slice 2) so
// a sales-organization has one place to set "what's our standard cut"
// instead of every rep re-entering it on every opportunity.
//
// Editing the per-opportunity percentages still warns the user (per the
// doc's "editable only with warning" requirement); defaults flow from
// here so the warning's reference point is the org standard.
//
// Self-contained so the existing `useOrgSettingsSection` hook + the
// `admin_update_org_settings` RPC stay untouched — this section uses a
// purpose-built RPC `opp_update_org_pricing_defaults` defined in
// `docs/sql/opportunity_foundation.sql`.

import { useCallback, useEffect, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

type LoadedDefaults = {
  default_commission_pct: number | null;
  default_margin_pct: number | null;
};

const BLANK: LoadedDefaults = {
  default_commission_pct: null,
  default_margin_pct: null,
};

export function OrgSettingsSectionOpportunityDefaults(props: {
  sb: SupabaseClient;
  organizationId: string;
}) {
  const { sb, organizationId } = props;
  const [original, setOriginal] = useState<LoadedDefaults>(BLANK);
  const [commission, setCommission] = useState("");
  const [margin, setMargin] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await sb
        .from("organizations")
        .select("default_commission_pct, default_margin_pct")
        .eq("id", organizationId)
        .maybeSingle();
      if (error) throw error;
      const row = (data ?? {}) as Partial<LoadedDefaults>;
      const loaded: LoadedDefaults = {
        default_commission_pct:
          typeof row.default_commission_pct === "number" ? row.default_commission_pct : null,
        default_margin_pct:
          typeof row.default_margin_pct === "number" ? row.default_margin_pct : null,
      };
      setOriginal(loaded);
      setCommission(loaded.default_commission_pct == null ? "" : String(loaded.default_commission_pct));
      setMargin(loaded.default_margin_pct == null ? "" : String(loaded.default_margin_pct));
    } catch (e) {
      const errMsg = e instanceof Error ? e.message : String(e);
      // Tolerate the "column does not exist" case: the migration hasn't
      // been applied yet in this environment. UI just stays blank and the
      // save button surfaces the same error if/when the user tries it.
      if (!/does not exist|schema cache|Could not find/i.test(errMsg)) {
        setMsg({ text: errMsg, ok: false });
      }
    } finally {
      setLoading(false);
    }
  }, [organizationId, sb]);

  useEffect(() => {
    void load();
  }, [load]);

  function parseOrNull(raw: string): number | null {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    const n = Number(trimmed);
    if (!Number.isFinite(n)) return null;
    return n;
  }

  function validate(): string | null {
    const c = parseOrNull(commission);
    const m = parseOrNull(margin);
    if (commission.trim() && c == null) return "Commission % must be a number";
    if (margin.trim() && m == null) return "Margin % must be a number";
    if (c != null && (c < 0 || c > 100)) return "Commission % must be between 0 and 100";
    if (m != null && (m < 0 || m > 100)) return "Margin % must be between 0 and 100";
    return null;
  }

  const parsedCommission = parseOrNull(commission);
  const parsedMargin = parseOrNull(margin);
  const isDirty =
    parsedCommission !== original.default_commission_pct ||
    parsedMargin !== original.default_margin_pct;

  async function save() {
    setMsg(null);
    const validationError = validate();
    if (validationError) {
      setMsg({ text: validationError, ok: false });
      return;
    }
    setSaving(true);
    try {
      const { error } = await sb.rpc("opp_update_org_pricing_defaults", {
        p_org_id: organizationId,
        p_default_commission_pct: parsedCommission,
        p_default_margin_pct: parsedMargin,
      });
      if (error) throw error;
      setOriginal({
        default_commission_pct: parsedCommission,
        default_margin_pct: parsedMargin,
      });
      setMsg({ text: "Opportunity Engine defaults saved.", ok: true });
    } catch (e) {
      const errMsg = e instanceof Error ? e.message : String(e);
      setMsg({ text: errMsg, ok: false });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="pt-2 border-t border-gray-100">
      <div className="mb-2">
        <h3 className="text-sm font-semibold text-gray-700">Opportunity Engine defaults</h3>
        <p className="text-xs text-gray-400 mt-0.5">
          Prefilled into the Opportunity pricing form for new opportunities. Reps can
          still override per opportunity (with a warning). Leave blank to skip the
          prefill and require entry on each opportunity.
        </p>
      </div>

      {msg && (
        <div
          className={`text-xs rounded-lg px-3 py-2 border mb-2 ${
            msg.ok
              ? "bg-emerald-50 border-emerald-200 text-emerald-800"
              : "bg-red-50 border-red-200 text-red-700"
          }`}
        >
          {msg.text}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-xs font-medium text-gray-600">Default commission %</label>
          <input
            type="number"
            inputMode="decimal"
            min={0}
            max={100}
            step="0.01"
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
            placeholder="e.g. 10"
            value={commission}
            onChange={(e) => setCommission(e.target.value)}
            disabled={loading || saving}
          />
          <p className="text-xs text-gray-400">0–100. Applies as a % of total sale price.</p>
        </div>

        <div className="space-y-1">
          <label className="text-xs font-medium text-gray-600">Default margin %</label>
          <input
            type="number"
            inputMode="decimal"
            min={0}
            max={100}
            step="0.01"
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2"
            placeholder="e.g. 12"
            value={margin}
            onChange={(e) => setMargin(e.target.value)}
            disabled={loading || saving}
          />
          <p className="text-xs text-gray-400">0–100. Applies on top of total cost.</p>
        </div>
      </div>

      <div className="flex items-center gap-2 pt-2">
        <button
          type="button"
          onClick={() => void save()}
          disabled={loading || saving || !isDirty}
          className="px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700 disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save defaults"}
        </button>
        {isDirty && (
          <button
            type="button"
            onClick={() => {
              setCommission(
                original.default_commission_pct == null ? "" : String(original.default_commission_pct),
              );
              setMargin(
                original.default_margin_pct == null ? "" : String(original.default_margin_pct),
              );
              setMsg(null);
            }}
            className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm hover:bg-gray-50"
          >
            Discard
          </button>
        )}
      </div>
    </div>
  );
}
